import { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { Box, Dialog, H2, Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import { useColorMode } from '@betterment/shared-components/src/components/color-mode/ColorModeProvider';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';

import { getEnvelopeAccountName, getRowSubtext } from './components/AccountRow';
import { LeadingIcon } from './components/LeadingIcon';
import { UpdateEnvelopeOrdersDocument } from './graphql/mutation.updateEnvelopeOrder.generated';
import { EnvelopesCardQuery } from './graphql/query.envelopesCard.generated';

import { EnvelopeOrderInput } from '../../graphql/schema';

type Envelope = EnvelopesCardQuery['envelopes'][number];

const DraggableEnvelopeRow: React.FC<{ envelope: Envelope }> = ({ envelope }) => {
  const { colorMode } = useColorMode();
  const { accounts, accountsDescription, purpose } = envelope;

  if (!purpose) {
    return (
      <>
        {accounts.map(account => {
          if (account.__typename === 'CheckingAccount') {
            return null;
          }

          return (
            <ListRow key={account.id} css={{ py: 'space.400', pl: 'space.0' }}>
              <ListRow.LeadingAsset>
                <LeadingIcon url={account.icon[colorMode]} />
              </ListRow.LeadingAsset>
              <ListRow.LeadingContent>
                <Text>{getEnvelopeAccountName(account)}</Text>
                <Text variant="text50" color="text.weak">
                  {getRowSubtext(account)}
                </Text>
              </ListRow.LeadingContent>
              <ListRow.TrailingContent>
                <Icon name="DragIndicator" size="small" />
              </ListRow.TrailingContent>
            </ListRow>
          );
        })}
      </>
    );
  }

  return (
    <ListRow css={{ py: 'space.200', pl: 'space.0' }}>
      <ListRow.LeadingAsset>
        <LeadingIcon url={purpose.image[colorMode]} />
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text>{purpose.name}</Text>
        {accountsDescription && (
          <Text variant="text50" color="text.weak">
            {accountsDescription}
          </Text>
        )}
      </ListRow.LeadingContent>
      <ListRow.TrailingContent>
        <Icon name="DragIndicator" size="small" />
      </ListRow.TrailingContent>
    </ListRow>
  );
};

export const EnvelopesReorderDialog: React.FC<{ envelopes: Envelope[] }> = ({ envelopes }) => {
  const [orderedEnvelopes, setOrderedEnvelopes] = useState(
    envelopes.filter(envelope => !envelope.accounts.some(account => account.__typename === 'CheckingAccount'))
  );
  const [updateEnvelopeOrder] = useGraphQLMutation(UpdateEnvelopeOrdersDocument);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    // Dropped outside the list
    if (!destination) {
      return;
    }

    // No position change
    if (destination.index === source.index) {
      return;
    }

    // Reorder the array
    const reorderedEnvelopes = Array.from(orderedEnvelopes);
    const [removed] = reorderedEnvelopes.splice(source.index, 1);
    reorderedEnvelopes.splice(destination.index, 0, removed);

    setOrderedEnvelopes(reorderedEnvelopes);
  };

  const handleSave = async () => {
    const orderedEnvelopesIndices: EnvelopeOrderInput[] = orderedEnvelopes.map((envelope, index) => ({
      envelopeId: envelope.id,
      orderPosition: index,
    }));

    await updateEnvelopeOrder({
      variables: {
        input: {
          envelopes: orderedEnvelopesIndices,
        },
      },
    });
  };

  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <Icon name="Preferences" size="small" />
      </Dialog.Trigger>
      <Dialog.Content css={{ p: 'space.400', overflow: 'hidden', borderRadius: 'large' }} size="large">
        <Dialog.Title>
          <H2 variant="title300" css={{ mb: 'space.300' }}>
            Reorder accounts
          </H2>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <Box css={{ overflow: 'hidden', height: '100%' }}>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="envelopes-list">
                {droppableProvided => (
                  <Box
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    css={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '50vh' }}
                  >
                    {orderedEnvelopes.map((envelope, index: number) => (
                      <Draggable key={envelope.id} draggableId={envelope.id} index={index}>
                        {(draggableProvided, snapshot) => {
                          const { style } = draggableProvided.draggableProps;
                          const transform = style?.transform;

                          // Keep the row in place horizontally during drag
                          const modifiedStyle =
                            snapshot.isDragging && transform
                              ? {
                                  ...style,
                                  left: 'auto',
                                  transform: `translate(0px, ${transform.match(/translate\([^,]+,\s*([^)]+)\)/)?.[1] || '0px'})`,
                                  zIndex: 9999,
                                }
                              : style;

                          return (
                            <Box
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                              style={modifiedStyle}
                            >
                              <DraggableEnvelopeRow envelope={envelope} />
                            </Box>
                          );
                        }}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </Box>
                )}
              </Droppable>
              {envelopes.some(envelope =>
                envelope.accounts.some(account => account.__typename === 'CheckingAccount')
              ) && (
                <>
                  <Separator />
                  <Text as="p" variant="text50" color="text.weak" css={{ my: 'space.200' }}>
                    Checking and joint checking accounts cannot be sorted at this time.
                  </Text>
                </>
              )}
            </DragDropContext>
          </Box>
        </Dialog.Description>
        <Dialog.Close onClick={handleSave}>Save</Dialog.Close>
      </Dialog.Content>
    </Dialog>
  );
};
